import React, { type ReactNode } from "react"
import { PlayFill } from "react-bootstrap-icons"
import { useNavigateToPost } from "@hooks/useNavigateToPost"
import styles from "./ExploreCard.module.css"

interface ExploreCardProps {
  imageUrl: string | null | undefined
  title: string
  subtitle?: string | null
  details?: ReactNode
  hoverDescription?: string | null
  light?: boolean
  onPlayClick?: (e: React.MouseEvent) => void
  to: string
}

export const ExploreCard: React.FC<ExploreCardProps> = ({
  imageUrl,
  title,
  subtitle,
  details,
  hoverDescription,
  light,
  onPlayClick,
  to,
}) => {
  // Always navigate via useNavigateToPost (not a bare <Link>) so
  // location.state.from is set - PostScreen's own Back button relies on
  // that to history.goBack() instead of pushing home.
  const navigateToPostScreen = useNavigateToPost(to)

  return (
    <div className={styles.card}>
      <div
        className={`${styles.cardLink} cursor-pointer`}
        onClick={navigateToPostScreen}
      >
        <div className={styles.imageWrap}>
          <img
            src={imageUrl ?? undefined}
            alt={title}
            className={`${styles.image} object-cover rounded-[6px] bg-[#adadad]`}
          />
          {hoverDescription && (
            <div className={styles.hoverMask}>
              <span className={styles.hoverMaskText}>
                {hoverDescription}
              </span>
            </div>
          )}
          {onPlayClick && (
            <button
              type="button"
              className={styles.playButton}
              aria-label={`Play ${title}`}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onPlayClick(e)
              }}
            >
              <PlayFill size="60%" />
            </button>
          )}
        </div>
        <div
          className={`${styles.titleClamp} mt-1.5 text-[13px] font-semibold leading-tight ${
            light ? "text-white" : "text-black-800"
          }`}
        >
          {title}
        </div>
        {subtitle && (
          <div
            className={`truncate text-[11px] mt-0.5 ${
              light ? "text-white/80" : "text-[#9c9c9c]"
            }`}
          >
            {subtitle}
          </div>
        )}
      </div>
      {details}
    </div>
  )
}
